import { memo, useEffect, useMemo, useState } from 'react';
import type { ReportGranularity, ReportPeriodDTO } from '@six-pockets/shared';
import { Pagination } from '@/components/ui';
import { cx } from '@/utils/cx';
import { formatINR } from '@/utils/format';
import { currentKey, periodLabel } from '../utils';
import styles from '@/styles/components/ReportTable/style.scss';

const PAGE_SIZE = 12;

interface ReportTableProps {
  periods: ReportPeriodDTO[];
  granularity: ReportGranularity;
}

/**
 * One row per day (or month) of a range, newest first, 12 to a page. The rows are already loaded (the trend chart
 * draws the same periods), so paging happens here. On a narrow list each row becomes a small labelled card.
 */
export const ReportTable = memo(function ReportTable({ periods, granularity }: ReportTableProps) {
  const rows = useMemo(() => [...periods].reverse(), [periods]);
  const [page, setPage] = useState(1);
  useEffect(() => setPage(1), [periods]);

  const current = currentKey(granularity);
  const shown = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <>
      <div className={styles.wrap}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>{granularity === 'day' ? 'Date' : 'Month'}</th>
              <th className={styles.num}>Revenue</th>
              <th className={styles.num}>Bookings</th>
              <th className={styles.num}>Condiments</th>
              <th className={styles.num}>Cond. profit</th>
              <th className={styles.num}>Discount</th>
              <th className={styles.num}>Due now</th>
              <th className={styles.num}>Collected</th>
              <th className={styles.num}>Bills</th>
            </tr>
          </thead>
          <tbody>
            {shown.map((p) => (
              <tr key={p.key} className={cx(p.key === current && styles.current)}>
                <td className={styles.period}>
                  {periodLabel(granularity, p.key)}
                  {p.key === current && <span className={styles.now}>{granularity === 'day' ? 'Today' : 'This month'}</span>}
                </td>
                <td className={cx(styles.num, styles.revenue)} data-label="Revenue">
                  {formatINR(p.revenue)}
                </td>
                <td className={styles.num} data-label="Bookings">
                  {formatINR(p.bookingsRevenue)}
                </td>
                <td className={styles.num} data-label="Condiments">
                  {formatINR(p.condimentsRevenue)}
                </td>
                <td className={styles.num} data-label="Cond. profit">
                  {formatINR(p.condimentsProfit)}
                </td>
                <td className={styles.num} data-label="Discount">
                  {p.discount > 0 ? formatINR(p.discount) : '—'}
                </td>
                <td className={cx(styles.num, p.balanceNow > 0 && styles.due)} data-label="Due now">
                  {p.balanceNow > 0 ? formatINR(p.balanceNow) : '—'}
                </td>
                <td className={styles.num} data-label="Collected">
                  {formatINR(p.collection.total)}
                </td>
                <td className={cx(styles.num, styles.count)} data-label="Bills">
                  {p.bills}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Pagination page={page} pageSize={PAGE_SIZE} total={rows.length} onPageChange={setPage} />
    </>
  );
});
